import React, { useEffect, useState } from 'react';
import { X, Loader2, ShieldAlert, CalendarClock, Globe } from 'lucide-react';
import api, { errText } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const money = (cur, n) => `${(cur || 'usd').toUpperCase()} ${Number(n).toFixed(2)}`;
const optLabel = (cur, s) => {
  const price = Number(s.price || 0);
  const dur = s.duration_min ? ` · ${s.duration_min} min` : '';
  return `${s.name}${dur} — ${price > 0 ? money(cur, price) : 'Free'}`;
};
const localTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } };
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayLabel = (d) => d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const timeLabel = (d) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

const PrivateLessonModal = ({ teacher, onClose }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [waiver, setWaiver] = useState('');
  const currency = teacher.private_lesson_currency || 'usd';
  const options = teacher.session_types || [];
  const [form, setForm] = useState({
    name: user?.full_name || '', email: user?.email || '', phone: '',
    purpose: 'consulting', preferred_datetime: '', message: '',
  });
  const [selectedId, setSelectedId] = useState(options[0]?.id || '');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  // slots
  const [slotMode, setSlotMode] = useState(null);      // null=loading, 'slots', 'legacy'
  const [slotsByDay, setSlotsByDay] = useState({});    // { 'YYYY-MM-DD': [{iso, label}] }
  const [facTz, setFacTz] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const selected = options.find((o) => o.id === selectedId) || options[0];
  const effectiveFree = !selected || Number(selected.price || 0) <= 0;

  useEffect(() => { api.get('/consultations/waiver').then((r) => setWaiver(r.data.text || '')).catch(() => {}); }, []);

  // Lock the background page scroll while the modal is open (prevents the "scroll behind" jank).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Load slots whenever the chosen session option changes.
  useEffect(() => {
    if (!selected) return;
    setLoadingSlots(true); setSelectedSlot(''); setSelectedDay('');
    api.get(`/consultations/${teacher.teacher_id || teacher.id}/slots`, { params: { session_type_id: selected.id } })
      .then((r) => {
        const d = r.data;
        setSlotMode(d.mode); setFacTz(d.timezone || '');
        if (d.mode === 'slots') {
          const grouped = {};
          (d.slots || []).forEach((iso) => {
            const dt = new Date(iso);
            const k = dayKey(dt);
            (grouped[k] = grouped[k] || []).push({ iso, dt });
          });
          setSlotsByDay(grouped);
          const firstDay = Object.keys(grouped).sort()[0] || '';
          setSelectedDay(firstDay);
        }
      })
      .catch(() => setSlotMode('legacy'))
      .finally(() => setLoadingSlots(false));
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!selected) return toast({ title: 'Please choose a session option' });
    if (!form.name || !form.email) return toast({ title: 'Please fill your name and email' });
    if (!form.phone || !form.phone.trim()) return toast({ title: 'Please enter your phone number' });
    if (slotMode === 'slots') { if (!selectedSlot) return toast({ title: 'Please pick an available time slot' }); }
    else if (!form.preferred_datetime) return toast({ title: 'Please choose a preferred time' });
    if (!agreed) return toast({ title: 'Please accept the booking disclaimer' });
    setBusy(true);
    try {
      const payload = {
        teacher_id: teacher.teacher_id || teacher.id, ...form,
        session_type_id: selected.id, waiver_agreed: true, origin_url: window.location.origin,
      };
      if (slotMode === 'slots') { payload.slot_start = selectedSlot; delete payload.preferred_datetime; }
      const r = await api.post('/consultations/request', payload);
      if (r.data.free) {
        toast({ title: 'Request sent 🌸', description: 'The facilitator will confirm your session shortly.' });
        onClose();
      } else if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) { toast({ title: 'Could not submit', description: errText(e) }); setBusy(false); }
  };

  const days = Object.keys(slotsByDay).sort();

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="private-lesson-modal" onClick={onClose}>
      <div className="bg-cream rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto overscroll-contain" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Request a private session</h2>
          <button onClick={onClose} data-testid="pl-close"><X className="w-5 h-5 text-mahogany/70" /></button>
        </div>
        <p className="text-sm text-mahogany/75">with <b>{teacher.name}</b></p>

        <div className="mt-4">
          <span className="text-xs uppercase tracking-widest text-mahogany/70">Choose a session</span>
          <div className="mt-1.5 space-y-2" data-testid="pl-options">
            {options.map((o) => (
              <label key={o.id} className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${selectedId === o.id ? 'bg-rose-soft/40 border-rose' : 'bg-white border-rose/40'}`} data-testid={`pl-option-${o.id}`}>
                <input type="radio" name="session-type" checked={selectedId === o.id} onChange={() => setSelectedId(o.id)} className="mt-1" />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-burgundy">{optLabel(currency, o)}</span>
                  {o.description && <span className="block text-xs text-mahogany/70 mt-0.5">{o.description}</span>}
                </span>
              </label>
            ))}
            {options.length === 0 && <p className="text-sm text-mahogany/60">No session options are available right now.</p>}
          </div>
        </div>

        {/* Time selection */}
        <div className="mt-4">
          <span className="text-xs uppercase tracking-widest text-mahogany/70">Pick a time</span>
          {loadingSlots && <div className="mt-2 flex items-center gap-2 text-sm text-mahogany/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading available times…</div>}
          {!loadingSlots && slotMode === 'slots' && (
            days.length === 0 ? (
              <p className="mt-2 text-sm text-mahogany/60" data-testid="pl-no-slots">No open times in the next 12 weeks. Please check back later.</p>
            ) : (
              <div className="mt-2" data-testid="pl-slot-picker">
                <div className="flex gap-2 overflow-x-auto pb-2" data-testid="pl-days">
                  {days.map((k) => {
                    const d = slotsByDay[k][0].dt;
                    return (
                      <button key={k} type="button" onClick={() => { setSelectedDay(k); setSelectedSlot(''); }}
                        className={`shrink-0 px-3 py-2 rounded-2xl border text-xs transition-colors ${selectedDay === k ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50'}`}
                        data-testid={`pl-day-${k}`}>{dayLabel(d)}<br /><span className="opacity-70">{slotsByDay[k].length} open</span></button>
                    );
                  })}
                </div>
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="pl-times">
                  {(slotsByDay[selectedDay] || []).map(({ iso, dt }) => (
                    <button key={iso} type="button" onClick={() => setSelectedSlot(iso)}
                      className={`px-2 py-1.5 rounded-lg border text-xs transition-colors ${selectedSlot === iso ? 'bg-rose text-cream border-rose' : 'bg-white text-mahogany border-rose/50 hover:bg-rose-soft/40'}`}
                      data-testid={`pl-slot-${iso}`}>{timeLabel(dt)}</button>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-mahogany/55 flex items-center gap-1"><Globe className="w-3 h-3" /> Times shown in your local time ({localTz()}){facTz ? ` · facilitator is in ${facTz}` : ''}</p>
              </div>
            )
          )}
          {!loadingSlots && slotMode === 'legacy' && (
            <label className="mt-2 block text-xs text-mahogany/70">Preferred date &amp; time
              <input type="datetime-local" value={form.preferred_datetime} onChange={(e) => set('preferred_datetime', e.target.value)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="pl-datetime" />
            </label>
          )}
        </div>

        <div className="mt-4 grid gap-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name" className="bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="pl-name" />
            <input value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="Email" className="bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="pl-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone number" className="bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="pl-phone" />
            <select value={form.purpose} onChange={(e) => set('purpose', e.target.value)} className="bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="pl-purpose">
              <option value="consulting">Consultation</option>
              <option value="meditation_class">Meditation class</option>
            </select>
          </div>
          <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={3} placeholder="What would you like to focus on?" className="bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="pl-message" />
        </div>

        <div className="mt-4 rounded-2xl bg-rose-soft/30 border border-rose/40 p-4">
          <div className="flex items-center gap-2 text-burgundy font-medium mb-2 text-sm"><ShieldAlert className="w-4 h-4" /> Booking disclaimer &amp; waiver</div>
          <details className="mt-1 group">
            <summary className="text-xs text-rose-italic cursor-pointer select-none list-none hover:underline">Read the full disclaimer &amp; waiver</summary>
            <p className="mt-2 text-xs text-mahogany/75 leading-relaxed whitespace-pre-line">{waiver}</p>
          </details>
          <label className="mt-3 flex items-start gap-2 text-sm text-mahogany cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="pl-agree" />
            <span>I have read and agree. I understand sessions are delivered by an independent facilitator and I take part at my own risk.</span>
          </label>
        </div>

        {!effectiveFree && <p className="mt-3 text-xs text-mahogany/60">Your card is authorized now (a hold). You're charged only when the facilitator accepts; if declined, the hold is released.</p>}
        <button onClick={submit} disabled={busy || !agreed || !selected} className="pill-btn mt-4 w-full justify-center disabled:opacity-50" data-testid="pl-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {effectiveFree ? 'Send request' : `Continue to authorize ${money(currency, selected?.price || 0)}`}
        </button>
      </div>
    </div>
  );
};

export default PrivateLessonModal;
